import type { AuthoritativeMapDefinition } from '../../spacetime/SpaceTimeClient';
import {
  buildCanonicalMapDocumentFromAuthoritative,
  BIOME_LEGEND,
  TILE_LEGEND,
} from '../../mapFormat/canonicalMapFormat.js';
import { getMapAssetRegistryEntry } from '../../maps/registry';
import { buildCanonicalMapObjectRecords } from '../../maps/mapObjectPersistence';
import type { BiomeType, MapDefinition } from '../../model/gamestate/GameState';
import { canonicalizePathTileLayers } from '../../terrain/pathAutotiler/pathTileLayers';

export type MapAssetDocument = ReturnType<typeof buildCanonicalMapDocumentFromAuthoritative>;

interface FilePickerWritable {
  write(data: string): Promise<void>;
  close(): Promise<void>;
}

interface FilePickerHandle {
  createWritable(): Promise<FilePickerWritable>;
}

type WindowWithFilePicker = Window & {
  showSaveFilePicker?: (options?: {
    id?: string;
    suggestedName?: string;
    startIn?: 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos';
    types?: Array<{ description?: string; accept: Record<string, string[]> }>;
  }) => Promise<FilePickerHandle>;
};

export async function buildFrontendMapAssetDocument(
  source: AuthoritativeMapDefinition,
  map: MapDefinition,
): Promise<MapAssetDocument> {
  const exportedObjects = buildCanonicalMapObjectRecords(source.objects);
  const biomeStorage = buildBiomeStorageForSave(map);
  const document = buildCanonicalMapDocumentFromAuthoritative(
    {
      ...source,
      payload: source.payload.type === 'blob'
        ? {
            type: 'blob',
            tiles: Uint8Array.from(map.tiles.flatMap((row) => row.map((tile) => terrainToTileValue(tile.terrain)))),
            biomes: Uint8Array.from(map.tiles.flatMap((row) => row.map((tile) => biomeToValue(tile.biome)))),
          }
        : source.payload,
      objects: exportedObjects,
    },
    buildFrontendRenderSection(source.render, map),
  );
  return {
    ...document,
    defaultBiome: biomeStorage.defaultBiome,
    biomeRegions: biomeStorage.biomeRegions,
    biomeOverrides: biomeStorage.biomeOverrides,
  };
}

function terrainToTileValue(terrain: MapDefinition['tiles'][number][number]['terrain']): number {
  switch (terrain) {
    case 'wall':
      return TILE_LEGEND.wall;
    case 'portal':
      return TILE_LEGEND.portal;
    case 'path':
      return TILE_LEGEND.path;
    case 'floor':
    default:
      return TILE_LEGEND.floor;
  }
}

function biomeToValue(biome: BiomeType | undefined): number {
  if (!biome) {
    return BIOME_LEGEND.default;
  }
  return BIOME_LEGEND[biome] ?? BIOME_LEGEND.default;
}

function buildFrontendRenderSection(render: unknown, map: MapDefinition): unknown {
  const canonicalPathLayers = canonicalizePathTileLayers(map.width, map.height, map.pathLayers, map.gridType);
  const pathBorderContinuations = Array.isArray(map.pathBorderContinuations)
    ? map.pathBorderContinuations.map((tile) => ({
        atlasId: tile.atlasId,
        x: tile.x,
        y: tile.y,
      }))
    : [];
  if (!render || typeof render !== 'object') {
    return canonicalPathLayers.length > 0 || pathBorderContinuations.length > 0
      ? {
          tileWidth: 32,
          tileHeight: 32,
          layers: [],
          pathLayers: canonicalPathLayers.map((layer) => ({
            layerId: layer.layerId,
            atlasId: layer.atlasId,
            width: layer.width,
            height: layer.height,
            data: layer.data.map((cell) => cell ? {
              mode: cell.mode,
              ...(cell.surfaceTerrain ? { surfaceTerrain: cell.surfaceTerrain } : {}),
              ...(cell.mode === 'fixed'
                ? {
                    variantId: cell.variantId,
                    flipHorizontal: cell.flipHorizontal === true,
                    flipVertical: cell.flipVertical === true,
                    rotationSteps: cell.rotationSteps ?? 0,
                  }
                : {}),
            } : null),
          })),
          ...(pathBorderContinuations.length > 0 ? { pathBorderContinuations } : {}),
        }
      : render;
  }
  const renderRecord = render as Record<string, unknown>;
  const layers = Array.isArray(renderRecord.layers) ? renderRecord.layers : [];
  const authoredLayerByName = new Map((map.authoredTileMap?.layers ?? []).map((layer) => [layer.name, layer] as const));
  return {
    ...renderRecord,
    pathLayers: canonicalPathLayers.map((layer) => ({
      layerId: layer.layerId,
      atlasId: layer.atlasId,
      width: layer.width,
      height: layer.height,
      data: layer.data.map((cell) => cell ? {
        mode: cell.mode,
        ...(cell.surfaceTerrain ? { surfaceTerrain: cell.surfaceTerrain } : {}),
        ...(cell.mode === 'fixed'
          ? {
              variantId: cell.variantId,
              flipHorizontal: cell.flipHorizontal === true,
              flipVertical: cell.flipVertical === true,
              rotationSteps: cell.rotationSteps ?? 0,
            }
          : {}),
      } : null),
    })),
    ...(pathBorderContinuations.length > 0 ? { pathBorderContinuations } : {}),
    layers: layers.map((layer) => {
      if (!map.authoredTileMap) {
        return layer;
      }
      if (!layer || typeof layer !== 'object' || (layer as Record<string, unknown>).kind !== 'tile') {
        return layer;
      }
      const layerRecord = layer as Record<string, unknown>;
      const authoredLayer = authoredLayerByName.get(String(layerRecord.name ?? ''));
      if (!authoredLayer) {
        return layer;
      }
      return {
        ...layerRecord,
        data: authoredLayer.tiles.map((tile) => encodeTiledGid(tile)),
      };
    }),
  };
}

function encodeTiledGid(tile: NonNullable<MapDefinition['authoredTileMap']>['layers'][number]['tiles'][number]): number {
  let raw = tile.gid;
  if (tile.flipHorizontal) {
    raw |= 0x80000000;
  }
  if (tile.flipVertical) {
    raw |= 0x40000000;
  }
  if (tile.flipDiagonal) {
    raw |= 0x20000000;
  }
  return raw >>> 0;
}

export async function saveMapAssetDocument(document: MapAssetDocument): Promise<string> {
  const registryEntry = getMapAssetRegistryEntry(document.mapId);
  const filename = registryEntry.assetPath.split('/').pop() ?? `${document.mapId}.json`;
  const json = `${JSON.stringify(compactBiomeStorageForSave(document), null, 2)}\n`;
  const picker = (window as WindowWithFilePicker).showSaveFilePicker;
  if (picker) {
    const handle = await picker({
      id: registryEntry.pickerId,
      suggestedName: filename,
      startIn: 'downloads',
      types: [{ description: 'Map asset JSON', accept: { 'application/json': ['.json'] } }],
    });
    const writable = await handle.createWritable();
    await writable.write(json);
    await writable.close();
    return registryEntry.assetPath;
  }

  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
  return registryEntry.assetPath;
}

function compactBiomeStorageForSave(document: MapAssetDocument): Record<string, unknown> {
  const serialized: Record<string, unknown> = {
    ...document,
    defaultBiome: document.defaultBiome ?? 'default',
    biomeRegions: document.biomeRegions ?? [],
    biomeOverrides: document.biomeOverrides ?? {},
  };
  delete serialized.biomes;
  return serialized;
}

function buildBiomeStorageForSave(map: MapDefinition): {
  defaultBiome: BiomeType;
  biomeRegions: NonNullable<MapAssetDocument['biomeRegions']>;
  biomeOverrides: NonNullable<MapAssetDocument['biomeOverrides']>;
} {
  const defaultBiome = map.defaultBiome ?? inferMostCommonBiome(map);
  const biomeRegions = [...(map.biomeRegions ?? [])];
  const biomeOverrides: Record<string, BiomeType> = {};

  for (const row of map.tiles) {
    for (const tile of row) {
      const actualBiome = tile.biome ?? 'default';
      const expectedBiome = resolveBiomeFromSparseStorage(defaultBiome, biomeRegions, tile.x, tile.y);
      if (actualBiome !== expectedBiome) {
        biomeOverrides[`${tile.x},${tile.y}`] = actualBiome;
      }
    }
  }

  return {
    defaultBiome,
    biomeRegions,
    biomeOverrides,
  };
}

function inferMostCommonBiome(map: MapDefinition): BiomeType {
  const counts = new Map<BiomeType, number>();
  for (const row of map.tiles) {
    for (const tile of row) {
      const biome = tile.biome ?? 'default';
      counts.set(biome, (counts.get(biome) ?? 0) + 1);
    }
  }

  let best: BiomeType = 'default';
  let bestCount = -1;
  for (const [biome, count] of counts) {
    if (count > bestCount) {
      best = biome;
      bestCount = count;
    }
  }
  return best;
}

function resolveBiomeFromSparseStorage(
  defaultBiome: BiomeType,
  biomeRegions: readonly { x: number; y: number; width: number; height: number; biome: BiomeType }[],
  x: number,
  y: number,
): BiomeType {
  let resolved = defaultBiome;
  for (const region of biomeRegions) {
    if (x >= region.x && x < region.x + region.width && y >= region.y && y < region.y + region.height) {
      resolved = region.biome;
    }
  }
  return resolved;
}
